import { useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

interface ArchiveMessage {
  id: number;
  body: string;
  senderName: string;
  createdAt: string;
  kind: string;
  linkedTaskId: number | null;
  notes: { id: number; body: string; authorName: string; createdAt: string; mentions: number[] }[];
}
interface ArchivePage { messages: ArchiveMessage[]; nextBefore: number | null }

/** No live message/card/note controls: this surface only issues archive GETs. */
export function HouseChatArchive({ conversationId }: { conversationId: number }) {
  const query = useInfiniteQuery({
    queryKey: ["/api/app/house-chat-archives", conversationId],
    initialPageParam: null as number | null,
    queryFn: async ({ pageParam }): Promise<ArchivePage> => {
      const response = await fetch(`/api/app/house-chat-archives/${conversationId}${pageParam == null ? "" : `?before=${pageParam}`}`, { credentials: "include" });
      if (!response.ok) throw new Error("Could not load archived house chat.");
      return response.json();
    },
    getNextPageParam: (page) => page.nextBefore ?? undefined,
  });

  return (
    <section className="flex h-full flex-col gap-3" aria-label="Archived house chat">
      <p className="rounded-md border p-3 text-sm text-muted-foreground">
        Archived · read-only. This property was permanently deleted. Messages and host-only internal notes are retained as history.
      </p>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
        {query.isPending && <p role="status">Loading history…</p>}
        {query.isError && <div role="alert">Could not load history. <Button variant="outline" onClick={() => void query.refetch()}>Retry</Button></div>}
        {query.hasNextPage && <Button variant="outline" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>Load older messages</Button>}
        {query.data?.pages.slice().reverse().flatMap((page) => page.messages).map((message) => (
          <article key={message.id} className="rounded-md border p-3">
            <p className="text-sm font-semibold">{message.senderName} <time className="font-normal text-muted-foreground">{new Date(message.createdAt).toLocaleString()}</time></p>
            {(message.kind === "maintenance" || message.linkedTaskId != null) && <p className="text-xs text-muted-foreground">Maintenance{message.linkedTaskId != null ? ` · linked task #${message.linkedTaskId}` : ""}</p>}
            <p className="whitespace-pre-wrap break-words text-sm">{message.body}</p>
            {message.notes.map((note) => (
              <div key={note.id} className="mt-2 rounded border bg-muted p-2 text-sm">
                <p className="font-medium">Internal note · {note.authorName} · {new Date(note.createdAt).toLocaleString()}</p>
                <p className="whitespace-pre-wrap break-words">{note.body}</p>
                {note.mentions.length > 0 && <p className="text-xs text-muted-foreground">Historical mentions: {note.mentions.map((id) => `#${id}`).join(", ")}</p>}
              </div>
            ))}
          </article>
        ))}
        {query.data?.pages[0]?.messages.length === 0 && <p className="text-sm text-muted-foreground">No messages in this archive.</p>}
      </div>
    </section>
  );
}
